import styled from "@emotion/styled";
import { omit, pick } from "es-toolkit";
import { useCallback, useMemo, useState, type CSSProperties } from "react";
import { useSelector } from "react-redux";

import { Button, createAcceptanceModalDialogProps, useModalDialog } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { NodeTemplate, NodeTemplateQueryParams } from "@skilitics/nautilus-endpoint-definition";

import { TemplateImportError } from "../../../errors";
import { createNodeSelectionCriteriaFilter, type Filter, type ImportNodeTarget } from "../../../library";
import type { ImportNodeOptions } from "../../../slots/types";
import { useCanImportHere, type ImportTargetLayout } from "../../../slots/useCanImportHere";
import { getNodeSlotIdSelector, SlotPathError, type DesignerNodeData } from "../../../state";
import { useNodeTemplateCategories, useQueryNodeTemplates } from "../api";
import { EditTemplate } from "../edit-template";
import { Buttons, useCanWriteNodeTemplate } from "../shared";
import type { NodeTemplatesSearchCriteria } from "../types";
import { Categories } from "./Categories";
import { CreateCards } from "./CreateCards";
import { Filters } from "./Filters";
import { Heading } from "./Heading";
import { Results } from "./Results";
import { Scope } from "./Scope";
import { ScrollableContentWrapper } from "./shared";
import { TemplateCards } from "./TemplateCards";
import type { CreateType } from "./types";
import { useImportTemplate } from "./useImportTemplate";
import { useLibraryLabel } from "./useLibraryLabel";

const editStyle: CSSProperties = {
  marginInlineStart: "auto",
};

const modalDialogId = "importTemplateDialogId";

const Container = styled.div`
  display: flex;
  flex-direction: column;

  width: 1040px;
  max-width: 100%;
  height: 100%;

  background-color: ${({ theme }) => theme.surface};

  overflow: hidden;
`;

const Body = styled.div`
  display: flex;

  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;

  padding-bottom: 15px;
`;

const ActionButton = styled(Button)`
  height: 32px;
`;

type Mode = "edit" | "select";

type Props = {
  criteria: NodeTemplateQueryParams;
  target: ImportNodeTarget;
  targetLayout?: ImportTargetLayout;
  hideFilters?: boolean;
  onSelected: (newNode: DesignerNodeData) => void;
  onCreate?: (type: CreateType) => void;
} & ImportNodeOptions & {
    onClose: () => void;
  };

export const BrowseTemplates = ({
  criteria,
  target,
  targetLayout,
  onClose,
  onSelected,
  onCreate,
  ...importOptions
}: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const { openModalDialog, closeModalDialog } = useModalDialog();

  const [mode, setMode] = useState<Mode>("select");

  const [selectedTemplate, setSelectedTemplate] = useState<NodeTemplate>();
  const [selectedCreateType, setSelectedCreateType] = useState<CreateType>();

  const canEdit = useCanWriteNodeTemplate(selectedTemplate);

  const includeCriteria = useMemo(() => pick(criteria, ["include"]) ?? {}, [criteria]);
  const excludeCriteria = useMemo(() => pick(criteria, ["exclude"]) ?? {}, [criteria]);

  const [searchCriteria, setSearchCriteria] = useState<NodeTemplatesSearchCriteria>(
    omit(criteria, ["createdBy", "exclude"]),
  );

  const getSlotIdById = useSelector(getNodeSlotIdSelector);

  const importTarget = useMemo(
    () => ("slotForNodeId" in target ? { slotId: getSlotIdById(target.slotForNodeId)! } : target),
    [getSlotIdById, target],
  );

  const canImportHere = useCanImportHere({ criteria, target: importTarget, targetLayout });
  const importTemplate = useImportTemplate(importOptions);

  const handleCreateConfirmed = useCallback((createType: CreateType) => onCreate?.(createType), [onCreate]);

  const handleTemplateConfirmed = useCallback(
    async (template: NodeTemplate) => {
      if (!(template && canImportHere(template.content))) return;

      try {
        const addableNode = await importTemplate(template, importTarget);
        onSelected(addableNode);
      } catch (error) {
        let message = formatMessage("message");

        if (error instanceof TemplateImportError) {
          message = error.cause as string;
        } else if (error instanceof SlotPathError) {
          // slot path resolution has been canceled by the user
          return;
        }

        const acceptanceDialogProps = createAcceptanceModalDialogProps({
          header: formatMessage("header"),
          message,
          confirmLabel: formatMessage("close"),
          onConfirm: () => closeModalDialog(modalDialogId),
        });

        openModalDialog(acceptanceDialogProps, modalDialogId);
      } finally {
        onClose();
      }
    },
    [
      canImportHere,
      closeModalDialog,
      formatMessage,
      importTarget,
      importTemplate,
      onClose,
      onSelected,
      openModalDialog,
    ],
  );

  const queryOptions = useMemo(() => ({ ...includeCriteria, ...searchCriteria }), [includeCriteria, searchCriteria]);

  const { categories, status: categoriesStatus } = useNodeTemplateCategories();

  const { templates, status: templateStatus } = useQueryNodeTemplates(queryOptions);

  const handleDeselect = useCallback(() => {
    setSelectedTemplate(undefined);
    setSelectedCreateType(undefined);
  }, []);

  const handleSelectTemplate = useCallback(
    (template: NodeTemplate) => {
      handleDeselect();
      setSelectedTemplate(template);
    },
    [handleDeselect],
  );

  const handleSelectCreatorType = useCallback(
    (createType: CreateType) => {
      handleDeselect();
      setSelectedCreateType(createType);
    },
    [handleDeselect],
  );

  const handleCriteriaChange = useCallback(
    (newCriteria: NodeTemplatesSearchCriteria) => {
      handleDeselect();
      setSearchCriteria((current) => ({ ...current, ...newCriteria }));
    },
    [handleDeselect],
  );

  const handleChooseAction = useCallback(() => {
    if (selectedCreateType) {
      handleCreateConfirmed(selectedCreateType);
    } else if (selectedTemplate) {
      void handleTemplateConfirmed(selectedTemplate);
    }
  }, [handleCreateConfirmed, handleTemplateConfirmed, selectedCreateType, selectedTemplate]);

  const handleEdit = useCallback(() => setMode("edit"), []);

  const handleSelect = useCallback(() => setMode("select"), []);

  const libraryLabel = useLibraryLabel(criteria);

  const matchesExcludeCriteria = useMemo(
    () => (candidate: Parameters<Filter>[0]) => {
      const filter = createNodeSelectionCriteriaFilter(excludeCriteria);
      return !filter(candidate);
    },
    [excludeCriteria],
  );

  return (
    <Container data-test="activity-library-container">
      <Heading
        search={searchCriteria.search}
        onChange={handleCriteriaChange}
        libraryLabel={libraryLabel}
        onClose={onClose}
      />

      <Body>
        <Filters data-test="filters-container">
          <Scope scope={searchCriteria.scope} onChange={handleCriteriaChange} />

          <Categories
            category={searchCriteria.category}
            categories={categories}
            onChange={handleCriteriaChange}
            querying={categoriesStatus === "pending"}
          />
        </Filters>

        <Content data-test="content-container">
          {mode === "edit" && selectedTemplate && templateStatus !== "pending" && (
            <EditTemplate
              template={selectedTemplate}
              onSaved={handleSelect}
              onCancel={handleSelect}
              onRemoved={handleSelect}
              categories={categories}
            />
          )}

          {mode === "select" && (
            <>
              <ScrollableContentWrapper onClick={handleDeselect}>
                <CardContent>
                  {onCreate && (
                    <CreateCards
                      selected={selectedCreateType}
                      onSelect={handleSelectCreatorType}
                      onConfirm={handleCreateConfirmed}
                      hidden={matchesExcludeCriteria({ role: "activity-group" }) ? ["grouping"] : []}
                    />
                  )}

                  <Results
                    count={(templates ?? []).length}
                    querying={templateStatus === "pending"}
                    libraryLabel={libraryLabel}
                  />

                  <TemplateCards
                    querying={templateStatus === "pending"}
                    templates={templates}
                    onSelect={handleSelectTemplate}
                    onConfirm={handleTemplateConfirmed}
                    selectedTemplateId={selectedTemplate?.id}
                    isUnavailable={({ roles }) => matchesExcludeCriteria({ roles })}
                  />
                </CardContent>
              </ScrollableContentWrapper>

              <Buttons>
                <ActionButton onClick={onClose} data-test="cancel-button">
                  {formatMessage("cancel")}
                </ActionButton>

                {canEdit && (
                  <ActionButton
                    variant="contained"
                    disabled={!selectedTemplate}
                    style={editStyle}
                    onClick={handleEdit}
                    data-test="edit-button"
                  >
                    {formatMessage("edit")}
                  </ActionButton>
                )}

                <ActionButton
                  variant="contained"
                  disabled={!(selectedTemplate || selectedCreateType)}
                  onClick={handleChooseAction}
                  data-test="select-button"
                >
                  {formatMessage("select")}
                </ActionButton>
              </Buttons>
            </>
          )}
        </Content>
      </Body>
    </Container>
  );
};

const formatStrings = {
  cancel: {
    en: "Cancel",
  },
  select: {
    en: "Select",
  },
  edit: {
    en: "Edit",
  },
  header: {
    en: "Unable to import template",
  },
  message: {
    en: "The template cannot be imported here.",
  },
  close: {
    en: "Close",
  },
} satisfies FormatMessages;
